// Reader for asset packs sent by the server (see backend/pack.py):
//   "ABVP" | u32 LE header length | gzip(JSON header) | blob section
// Image previews in the header point into the blob section as [offset, length]; so does
// the "raw" of a TextAsset preview (the file's exact bytes). A text preview holds no text of
// its own: "script" is where it sits in the item's dump (see previewText).

async function gunzip(bytes) {
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream("gzip"));
  return new Response(stream).text();
}

/**
 * Parse a pack. Returns { assets, items: [[pathId, item], ...] } where each
 * item is { preview, dump, texture_options?, image? (Blob), raw? (Blob) }.
 */
export async function readPack(buffer) {
  const view = new DataView(buffer);
  const magic = String.fromCharCode(...new Uint8Array(buffer, 0, 4));
  if (magic !== "ABVP") throw new Error("The server sent an unexpected response");
  const length = view.getUint32(4, true);
  const header = JSON.parse(await gunzip(new Uint8Array(buffer, 8, length)));
  const blobs = 8 + length;
  const items = Object.entries(header.items || {}).map(([pid, item]) => {
    const preview = item.preview || { kind: "unsupported" };
    let image = null;
    let audio = null;
    let mesh = null;
    if (["image", "audio", "mesh"].includes(preview.kind) && Array.isArray(preview.blob)) {
      const [offset, size] = preview.blob;
      const type = preview.mime || { audio: "audio/wav", mesh: "application/octet-stream" }[preview.kind] || "image/webp";
      const blob = new Blob([buffer.slice(blobs + offset, blobs + offset + size)], { type });
      if (preview.kind === "audio") audio = blob;
      else if (preview.kind === "mesh") mesh = blob;
      else image = blob;
      delete preview.blob;
    }
    let raw = null;
    if (preview.kind === "text" && Array.isArray(preview.raw)) {
      const [offset, size] = preview.raw;
      raw = new Blob([buffer.slice(blobs + offset, blobs + offset + size)], { type: "application/octet-stream" });
      delete preview.raw;
    }
    // size: the asset's size as the item describes it (after a replace, the new one).
    return [pid, { preview, dump: item.dump ?? "", texture_options: item.texture_options ?? null, image, audio, mesh, raw, size: item.size ?? null }];
  });
  return { assets: header.assets ?? null, items };
}

/** The text of a text preview: the span of the dump that holds it (older packs carry it in the preview). */
export function previewText(item) {
  const p = item?.preview;
  if (p?.kind !== "text") return null;
  if (typeof p.text === "string") return p.text;
  const [start, end] = Array.isArray(p.script) ? p.script : [0, 0];
  return String(item.dump ?? "").slice(start, end);
}
